import { useId, useState, type ReactNode } from 'react';
import { ChevronRight, Loader2, Plus, X } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { Switch } from '@/components/ui/switch';
import { useSalesAgentSettings, useSaveSalesAgentSettings } from '@/hooks/use-sales-agent-settings';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { productRecommendationService } from '@/services/product-recommendation.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import type { SelectionKind } from '@/types';
import { DISCOUNT_CODE_MAX_LENGTH, type DiscountCodeSource, type DiscountSettings, type SalesAgentSettings } from '../../settings/types';
import { GHOST_FOCUS_RING } from '../../settings/ui-chrome';
import { PILL_BUTTON } from '../../ui-chrome';
import { useSavedDraft, type SavedDraftOptions } from '../../use-saved-draft';
import {
	FREQUENCY_DAY_OPTIONS,
	discountProblems,
	draftDiscount,
	sameAsSaved,
	sameDiscount,
	toCodeDraft,
	toCodeDrafts,
	toDiscountDraft,
	type DiscountDraft,
	type DiscountField,
	type DiscountProblems
} from '../discount-draft';
import { DiscountExclusionsSheet } from './discount-exclusions-sheet';
import { EXCLUSION_KINDS } from '../exclusion-kinds';

export const DISCOUNTING_NOTE = 'Limits below are saved now and apply the moment you switch this on.';

export const INHERITED_NOTE = 'This brand uses the account’s discount limits. Saving here gives it its own.';

const DISCOUNT_DRAFT: SavedDraftOptions<DiscountSettings, DiscountDraft> = {
	toDraft: toDiscountDraft,
	isUnchanged: sameAsSaved,
	sameSaved: sameDiscount
};

/** "3 products", "1 tag": an exclusion row's count as it reads. */
const exclusionCount = (count: number, label: string): string =>
	`${count} ${count === 1 ? label.toLowerCase().slice(0, -1) : label.toLowerCase()}`;

const SOURCES: { value: DiscountCodeSource; title: string; description: string }[] = [
	{ value: 'existing', title: 'Existing Discount Code', description: 'Predefined Discount codes applicable for all users' },
	{ value: 'generate', title: 'Generate Discount Codes', description: 'Codes will be generated dynamically using Shopify' }
];

// Figma's #eceef1 rule has no token: gray-200 (#edeff2) is the nearest, as on the Settings tab.
const CARD = 'flex w-full flex-col overflow-hidden rounded-2xl border border-gray-500 bg-white';
const ROW_TITLE = 'text-sm font-medium text-ink';
const ROW_HELP = 'text-xs leading-[18px] text-gray-800';
const SECTION_HEADING = 'text-[13px] font-medium text-gray-800';
const SMALL_FIELD = 'flex h-9 items-center gap-1.5 rounded-[10px] border border-gray-500 bg-white px-3 text-[13px]';
const BARE_INPUT = 'h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ink-strong shadow-none focus-visible:ring-0';

/**
 * The Sales Agent's Discount Limits tab (Figma Kim-AI 6849:15132, 6443:56991): whether the agent
 * may offer a discount, where its codes come from, how far it may go and what it never discounts.
 */
export const DiscountLimitsTab = () => {
	const { data, isLoading, isError } = useSalesAgentSettings();

	if (isLoading) return <div className='px-5 pt-6 text-xs text-gray-800'>Loading discount limits…</div>;
	if (isError || !data) return <div className='px-5 pt-6 text-xs text-red-600'>Could not load the discount limits.</div>;

	// Keyed so a brand switch starts from that brand's saved limits, not the previous brand's draft.
	return <DiscountLimits key={data.buId ?? 'account'} settings={data} />;
};

/**
 * Edits are a local draft until Save sends the whole discount in one PUT; the Settings tab's parts
 * are left alone, so the two tabs never overwrite each other.
 */
const DiscountLimits = ({ settings }: { settings: SalesAgentSettings }) => {
	const save = useSaveSalesAgentSettings();
	const scope = useAtomValue(selectedBusinessUnitIdAtom) ?? '';
	const { draft, isDirty, update: updateDraft, settle } = useSavedDraft(settings.discount, DISCOUNT_DRAFT);
	const [problems, setProblems] = useState<DiscountProblems>({});
	const [exclusionKind, setExclusionKind] = useState<SelectionKind | null>(null);
	const fieldsId = useId();
	/** The id of a field's problem text, for its input's aria-describedby; undefined while it has none. */
	const problemId = (field: DiscountField): string | undefined => (problems[field] ? `${fieldsId}-${field}-problem` : undefined);

	const update = (patch: Partial<DiscountDraft>) => {
		updateDraft(patch);
		setProblems({});
	};

	const onSave = () => {
		const found = discountProblems(draft);
		setProblems(found);
		if (Object.keys(found).length) return;
		const sent = draft;
		save.mutate(
			{ discount: draftDiscount(draft) },
			{
				onSuccess: (saved) => {
					// Only a draft untouched since Save is reset; edits made while saving stay.
					settle(sent, saved.discount);
					toast.success('Discount limits saved');
				},
				onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the discount limits'))
			}
		);
	};

	return (
		<div className='flex flex-col items-start px-5 pt-6 pb-6'>
			<div className='flex w-[600px] max-w-full flex-col gap-6'>
				<section className={CARD}>
					<div className='flex w-full items-center gap-6 border-b border-gray-200 px-4 py-3.5'>
						<h2 className={cn(ROW_TITLE, 'min-w-0 flex-1')}>Discounting</h2>
						<Switch
							checked={draft.enabled}
							onCheckedChange={(enabled) => update({ enabled })}
							aria-label='Let the agent offer discounts'
						/>
					</div>
					<p className='bg-gray-50 px-4 py-2.5 text-xs text-gray-800'>{DISCOUNTING_NOTE}</p>
				</section>
				{settings.inherited && <p className='text-xs leading-4 text-gray-800'>{INHERITED_NOTE}</p>}

				<CodeSourceCard draft={draft} problem={problems.codes} problemId={problemId('codes')} onChange={update} />

				<Section heading='Ceiling'>
					<LimitRow
						title='Maximum Discount Percentage'
						help='Maximum percentage in a single order'
						problem={problems.maxPercent}
						problemId={problemId('maxPercent')}
					>
						<div className={cn(SMALL_FIELD, 'w-[140px]')}>
							<Input
								aria-label='Maximum discount percentage'
								aria-invalid={problems.maxPercent ? true : undefined}
								aria-describedby={problemId('maxPercent')}
								inputMode='decimal'
								placeholder='No max'
								value={draft.maxPercent}
								onChange={(event) => update({ maxPercent: event.target.value })}
								className={cn(BARE_INPUT, 'placeholder:text-gray-800')}
							/>
							<span className='shrink-0 text-gray-800'>%</span>
						</div>
					</LimitRow>
					<LimitRow
						title='Upper Cap'
						help='Absolute discount should be less than or equal to this upper cap'
						problem={problems.maxAmount}
						problemId={problemId('maxAmount')}
					>
						{/* No currency sign: the store's currency is not known here, and the cap is in it. */}
						<div className={cn(SMALL_FIELD, 'w-[140px]')}>
							<Input
								aria-label='Upper cap'
								aria-invalid={problems.maxAmount ? true : undefined}
								aria-describedby={problemId('maxAmount')}
								inputMode='decimal'
								placeholder='No cap'
								value={draft.maxAmount}
								onChange={(event) => update({ maxAmount: event.target.value })}
								className={cn(BARE_INPUT, 'placeholder:text-gray-800')}
							/>
						</div>
					</LimitRow>
					<LimitRow title='Discount frequency' problem={problems.frequencyCount} problemId={problemId('frequencyCount')}>
						<div className='flex items-center gap-2'>
							<div className={cn(SMALL_FIELD, 'w-[60px] justify-center px-1.5')}>
								<Input
									aria-label='Discounts allowed'
									aria-invalid={problems.frequencyCount ? true : undefined}
									aria-describedby={problemId('frequencyCount')}
									inputMode='numeric'
									value={draft.frequencyCount}
									onChange={(event) => update({ frequencyCount: event.target.value })}
									className={cn(BARE_INPUT, 'text-center')}
								/>
							</div>
							<span className='text-[11px] leading-4 text-gray-800'>In</span>
							<Select
								value={String(draft.frequencyDays)}
								onValueChange={(value) => update({ frequencyDays: Number(value) })}
							>
								<SelectTrigger
									aria-label='Frequency window'
									className='h-9 gap-2 rounded-[10px] border-gray-500 bg-white pr-1.5 pl-1.5 text-[13px] text-ink-strong shadow-none'
								>
									<span className='flex items-center gap-1'>
										{draft.frequencyDays}
										<span className='text-[11px] leading-4 text-gray-800'>Days</span>
									</span>
								</SelectTrigger>
								<SelectContent>
									{FREQUENCY_DAY_OPTIONS.map((days) => (
										<SelectItem key={days} value={String(days)}>
											{days} Days
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</LimitRow>
				</Section>

				<Section heading='Exclusions'>
					{EXCLUSION_KINDS.map((kind) => {
						const count = exclusionCount(draft.exclusions[kind.key].length, kind.label);
						return (
							<button
								key={kind.key}
								type='button'
								aria-label={`Excluded ${kind.label.toLowerCase()}: ${count}`}
								onClick={() => setExclusionKind(kind.key)}
								className='flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-indigo-500'
							>
								<span className={cn(ROW_TITLE, 'min-w-0 flex-1')}>{kind.label}</span>
								<span className='text-xs text-gray-800'>{count}</span>
								<span className='flex size-7 items-center justify-center'>
									<ChevronRight className='size-3.5 text-gray-800' strokeWidth={1.4} />
								</span>
							</button>
						);
					})}
				</Section>

				<div className='flex w-full justify-end'>
					<Button
						type='button'
						disabled={!isDirty || save.isPending}
						onClick={onSave}
						className={cn(PILL_BUTTON, 'px-3')}
					>
						{save.isPending && <Loader2 className='size-3.5 animate-spin' />}
						Save
					</Button>
				</div>
			</div>

			<DiscountExclusionsSheet
				openKind={exclusionKind}
				exclusions={draft.exclusions}
				client={productRecommendationService}
				scope={scope}
				onApply={(exclusions) => {
					update({ exclusions });
					setExclusionKind(null);
				}}
				onClose={() => setExclusionKind(null)}
			/>
		</div>
	);
};

/** Existing codes or generated ones; with existing codes, the brand lists them here. */
const CodeSourceCard = ({
	draft,
	problem,
	problemId,
	onChange
}: {
	draft: DiscountDraft;
	problem?: string;
	problemId?: string;
	onChange: (patch: Partial<DiscountDraft>) => void;
}) => {
	const groupId = useId();
	const setText = (id: string, text: string) =>
		onChange({ codes: draft.codes.map((code) => (code.id === id ? { ...code, text } : code)) });
	const remove = (id: string) => {
		const next = draft.codes.filter((code) => code.id !== id);
		onChange({ codes: next.length ? next : toCodeDrafts([]) });
	};
	// One empty field at a time: a new one is offered once every field has text.
	const canAdd = draft.codes.every((code) => code.text.trim() !== '');

	return (
		<section className={cn(CARD, 'gap-4 px-4 py-3.5')}>
			<RadioGroup
				value={draft.source}
				onValueChange={(value) => onChange({ source: value as DiscountCodeSource })}
				aria-label='Where discount codes come from'
				className='gap-3'
			>
				{SOURCES.map((source, index) => (
					<div key={source.value} className='flex flex-col gap-3'>
						{index > 0 && <hr className='border-gray-200' />}
						<label htmlFor={`${groupId}-${source.value}`} className='flex cursor-pointer items-center gap-4'>
							<span className='flex size-6 shrink-0 items-center justify-center'>
								<RadioGroupItem
									id={`${groupId}-${source.value}`}
									value={source.value}
									className='size-3.5 border-gray-500 data-[state=checked]:border-indigo-500 [&_svg]:size-2 [&_svg]:fill-indigo-600 [&_svg]:stroke-none'
								/>
							</span>
							<span className='flex min-w-0 flex-1 flex-col gap-0.5'>
								<span className={ROW_TITLE}>{source.title}</span>
								<span className={ROW_HELP}>{source.description}</span>
							</span>
						</label>
					</div>
				))}
			</RadioGroup>

			{draft.source === 'existing' && (
				<>
					<hr className='border-gray-500' />
					<div className='flex flex-col gap-3'>
						{draft.codes.map((code, index) => (
							<div key={code.id} className='flex w-full items-end gap-3'>
								<label className='flex min-w-0 flex-1 flex-col gap-2'>
									<span className='text-xs leading-4 font-medium text-gray-900'>Discount Code {index + 1}</span>
									<Input
										value={code.text}
										maxLength={DISCOUNT_CODE_MAX_LENGTH}
										aria-invalid={problem ? true : undefined}
										aria-describedby={problemId}
										placeholder='Enter Discount code'
										onChange={(event) => setText(code.id, event.target.value)}
										className='h-10 rounded-xl border-gray-500 py-0 text-xs text-ink placeholder:text-gray-800'
									/>
								</label>
								<Button
									type='button'
									variant='ghost'
									size='icon'
									aria-label={`Remove discount code ${index + 1}`}
									onClick={() => remove(code.id)}
									className={cn('mb-1.5 size-7 rounded-full text-ink-strong hover:bg-gray-100', GHOST_FOCUS_RING)}
								>
									<X className='size-3.5' />
								</Button>
							</div>
						))}
						{problem && (
							<p id={problemId} className='text-xs leading-4 text-red-600'>
								{problem}
							</p>
						)}
						{canAdd && (
							<Button
								type='button'
								variant='outline'
								onClick={() => onChange({ codes: [...draft.codes, toCodeDraft('')] })}
								className='h-7 w-fit gap-2 rounded-full border-gray-500 bg-white px-3 text-[13px] font-normal text-ink-strong shadow-none hover:bg-gray-50'
							>
								<Plus className='size-3.5' /> Add Existing discount code
							</Button>
						)}
					</div>
				</>
			)}
		</section>
	);
};

const Section = ({ heading, children }: { heading: string; children: ReactNode }) => (
	<section className='flex w-full flex-col gap-3'>
		<h3 className={SECTION_HEADING}>{heading}</h3>
		<div className={cn(CARD, 'divide-y divide-gray-200')}>{children}</div>
	</section>
);

const LimitRow = ({
	title,
	help,
	problem,
	problemId,
	children
}: {
	title: string;
	help?: string;
	problem?: string;
	/** Set on the problem text, so the field can point at it with aria-describedby. */
	problemId?: string;
	children: ReactNode;
}) => (
	<div className='flex flex-col gap-1.5 px-4 py-3.5'>
		<div className='flex items-center gap-6'>
			<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
				<span className={ROW_TITLE}>{title}</span>
				{help && <span className={ROW_HELP}>{help}</span>}
			</div>
			{children}
		</div>
		{problem && (
			<p id={problemId} className='text-right text-xs leading-4 text-red-600'>
				{problem}
			</p>
		)}
	</div>
);
